import React, {useMemo, useState} from 'react';
import _ from 'lodash';
import {Link} from 'react-router-dom';
import {Grid, Header, Pagination, Table} from 'semantic-ui-react';

import Loader from '../../components/WandbLoader';
import * as Urls from '../../util/urls';
import * as ArtifactUtils from '../../util/artifacts';
import * as Generated from '../../generated/graphql';
import * as Obj from '../../util/obj';
import {capitalizeFirst} from '../../util/string';
import makeComp from '../../util/profiler';
import docUrl from '../../util/doc_urls';

const PAGE_SIZE = 500;

type ArtifactType = 'inputArtifacts' | 'outputArtifacts';
type ArtifactQueryType = ReturnType<typeof useRunArtifactVersions>['artifacts'];

const useRunArtifactVersions = (props: {
  entityName: string;
  projectName: string;
  runName: string;
  artifactsType: ArtifactType;
}) => {
  const {entityName, projectName, runName, artifactsType} = props;
  const [offset, setOffset] = useState(0);
  const q = Generated.useRunArtifactVersionsQuery({
    variables: {
      entityName,
      projectName,
      runName,
      pageSize: PAGE_SIZE,
      includeInput: artifactsType === 'inputArtifacts',
      includeOutput: artifactsType === 'outputArtifacts',
      inputOffset: offset,
      outputOffset: offset,
    },
  });
  const run = q.data?.project?.run;
  const artifacts =
    run?.[artifactsType]?.edges.map(e => e.node).filter(Obj.notEmpty) ?? [];
  const total = run?.[artifactsType]?.totalCount ?? 0;

  return {...q, artifacts, total, offset, setOffset};
};

interface RunArtifactsProps {
  entityName: string;
  projectName: string;
  runName: string;
}

export const RunArtifactGrid = makeComp<RunArtifactsProps>(
  props => {
    const inputArtifacts = useRunArtifactVersions({
      ...props,
      artifactsType: 'inputArtifacts',
    });
    const outputArtifacts = useRunArtifactVersions({
      ...props,
      artifactsType: 'outputArtifacts',
    });

    const loading = outputArtifacts.loading || inputArtifacts.loading;
    const hasArtifacts =
      outputArtifacts.total !== 0 || inputArtifacts.total !== 0;

    if (loading && hasArtifacts) {
      return (
        <div style={{position: 'relative', height: 300}}>
          <Loader />
        </div>
      );
    }
    if (!hasArtifacts) {
      return null;
    }

    const hasBothArtifacts =
      outputArtifacts.total > 0 && inputArtifacts.total > 0;
    return (
      <Grid.Row columns={hasBothArtifacts ? 2 : 1}>
        {outputArtifacts.total > 0 && (
          <Grid.Column>
            <Header>Artifact Outputs</Header>
            <p className="hint-text">
              This run produced these artifacts as outputs.{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={docUrl.artifacts}>
                Learn more
              </a>
            </p>
            <ArtifactsTable
              {...outputArtifacts}
              artifactsType="outputArtifacts"
              noHeader
            />
          </Grid.Column>
        )}
        {inputArtifacts.total > 0 && (
          <Grid.Column>
            <Header>Artifact Inputs</Header>
            <p className="hint-text">
              This run consumed these artifacts as inputs.{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={docUrl.artifacts}>
                Learn more
              </a>
            </p>
            <ArtifactsTable
              {...inputArtifacts}
              artifactsType="inputArtifacts"
              noHeader
            />
          </Grid.Column>
        )}
      </Grid.Row>
    );
  },
  {id: 'RunArtifactGrid'}
);

const RunArtifacts = makeComp(
  (props: RunArtifactsProps) => {
    const inputArtifacts = useRunArtifactVersions({
      ...props,
      artifactsType: 'inputArtifacts',
    });
    const outputArtifacts = useRunArtifactVersions({
      ...props,
      artifactsType: 'outputArtifacts',
    });

    return (
      <div style={{padding: 64}}>
        <ArtifactsTable {...inputArtifacts} artifactsType="inputArtifacts" />
        <ArtifactsTable {...outputArtifacts} artifactsType="outputArtifacts" />
      </div>
    );
  },
  {id: 'RunArtifacts'}
);

export default RunArtifacts;

interface ArtifactsTableProps {
  total: number;
  artifacts: ArtifactQueryType;
  artifactsType: ArtifactType;
  loading: boolean;
  offset: number;
  setOffset: (offset: number) => void;
  noHeader?: boolean;
}

const ArtifactsTable: React.FC<ArtifactsTableProps> = makeComp(
  props => {
    const {
      loading,
      total,
      offset,
      setOffset,
      artifacts,
      artifactsType,
      noHeader = false,
    } = props;

    const pagination = useMemo(() => makePagination(total, offset, setOffset), [
      total,
      offset,
      setOffset,
    ]);

    if (loading && total === 0) {
      return (
        <div style={{position: 'relative', height: 300}}>
          <Loader />
        </div>
      );
    }

    if (total === 0) {
      return null;
    }

    return (
      <>
        {!noHeader && (
          <Header as="h2">
            {capitalizeFirst(artifactsType.replace('Artifacts', ''))} artifacts
          </Header>
        )}
        {loading ? (
          <div style={{position: 'relative', height: 200}}>
            <Loader />
          </div>
        ) : (
          <Table selectable padded>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>

                <Table.HeaderCell>Consumer count</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {artifacts.map(a => {
                const {usedBy} = a;
                const displayAlias = ArtifactUtils.artifactNiceName(a);
                const url = Urls.artifact({
                  entityName: a.artifactType.project.entity.name,
                  projectName: a.artifactType.project.name,
                  artifactTypeName: a.artifactType.name,
                  artifactSequenceName: a.artifactSequence.name,
                  artifactCommitHash: a.commitHash!,
                });
                return (
                  <Table.Row key={a.id}>
                    <Table.Cell selectable>
                      <Link to={url}>{a.artifactType.name}</Link>
                    </Table.Cell>
                    <Table.Cell selectable>
                      <Link to={url}>{displayAlias}</Link>
                    </Table.Cell>

                    <Table.Cell selectable>
                      <Link to={url}>{usedBy.totalCount}</Link>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
        {pagination}
      </>
    );
  },
  {id: 'ArtifactsTable', memo: true}
);

function makePagination(
  total: number,
  offset: number,
  setOffset: (o: number) => void
) {
  return total > PAGE_SIZE ? (
    <div style={{display: 'flex', justifyContent: 'center'}}>
      <Pagination
        activePage={Math.floor(offset / PAGE_SIZE) + 1}
        totalPages={Math.ceil(total / PAGE_SIZE)}
        onPageChange={(e, data) => {
          const pg = data.activePage;
          if (pg != null && _.isNumber(pg)) {
            setOffset((pg - 1) * PAGE_SIZE);
          }
        }}
        size="small"
      />
    </div>
  ) : null;
}
